import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const AuthContext = createContext(null);
const apiRoot = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const refreshTokenStorageKey = 'ivr.refreshToken';
const clientPlatformHeaders = { 'X-Client-Platform': 'web' };

function getRefreshToken() {
  const persistentToken = window.localStorage.getItem(refreshTokenStorageKey);
  if (persistentToken) {
    return persistentToken;
  }

  const sessionToken = window.sessionStorage.getItem(refreshTokenStorageKey);
  if (sessionToken) {
    window.localStorage.setItem(refreshTokenStorageKey, sessionToken);
    window.sessionStorage.removeItem(refreshTokenStorageKey);
  }

  return sessionToken || '';
}

function setRefreshToken(refreshToken) {
  if (refreshToken) {
    window.localStorage.setItem(refreshTokenStorageKey, refreshToken);
  } else {
    window.localStorage.removeItem(refreshTokenStorageKey);
  }

  if (window.sessionStorage.getItem(refreshTokenStorageKey)) {
    window.sessionStorage.removeItem(refreshTokenStorageKey);
  }
}

function parseToken(token) {
  try {
    const unpaddedPayload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = unpaddedPayload.padEnd(Math.ceil(unpaddedPayload.length / 4) * 4, '=');
    return JSON.parse(decodeURIComponent(window.atob(payload).split('').map((character) =>
      `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')));
  } catch {
    return {};
  }
}

function tokenExpiresSoon(token) {
  const expiresAt = Number(parseToken(token).exp || 0) * 1000;
  return expiresAt <= Date.now() + 60_000;
}

async function readJson(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || 'Authentication request failed.');
  }
  return body;
}

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const tokenRef = useRef('');
  const refreshPromiseRef = useRef(null);

  const updateToken = useCallback((token) => {
    tokenRef.current = token || '';
    setAccessToken(token || '');
  }, []);

  const updateSession = useCallback((result) => {
    updateToken(result?.token);
    if (result?.refreshToken) {
      setRefreshToken(result.refreshToken);
    }
    return result;
  }, [updateToken]);

  const refreshAccessToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      updateToken('');
      throw new Error('No refresh token is available.');
    }

    refreshPromiseRef.current = fetch(`${apiRoot}/api/auth/refresh-token`, {
      method: 'POST',
      headers: {
        ...clientPlatformHeaders,
        'X-Refresh-Token': refreshToken
      }
    }).then(readJson)
      .then((result) => {
        updateSession(result);
        return result.token;
      })
      .catch((error) => {
        updateToken('');
        setRefreshToken('');
        throw error;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    return refreshPromiseRef.current;
  }, [updateSession, updateToken]);

  const getAccessToken = useCallback(async () => {
    if (tokenRef.current && !tokenExpiresSoon(tokenRef.current)) {
      return tokenRef.current;
    }
    return refreshAccessToken();
  }, [refreshAccessToken]);

  useEffect(() => {
    refreshAccessToken().catch(() => {}).finally(() => setIsLoading(false));
  }, [refreshAccessToken]);

  const sendLoginCode = useCallback(async (email) => {
    const response = await fetch(`${apiRoot}/api/auth/send-login-code`, {
      method: 'POST',
      headers: { ...clientPlatformHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return readJson(response);
  }, []);

  const verifyLoginCode = useCallback(async (email, otp, challengeId) => {
    const response = await fetch(`${apiRoot}/api/auth/verify-login-code`, {
      method: 'POST',
      headers: { ...clientPlatformHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp, challengeId })
    });
    const result = await readJson(response);
    updateSession(result);
    return result;
  }, [updateSession]);

  const googleLogin = useCallback(async (idToken) => {
    const response = await fetch(`${apiRoot}/api/auth/google-login`, {
      method: 'POST',
      headers: { ...clientPlatformHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken })
    });
    const result = await readJson(response);
    updateSession(result);
    return result;
  }, [updateSession]);

  const selectCompany = useCallback(async (companyId) => {
    const token = await getAccessToken();
    const response = await fetch(`${apiRoot}/api/auth/refresh-company-context`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...clientPlatformHeaders,
        'Content-Type': 'application/json',
        'X-Refresh-Token': getRefreshToken(),
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ companyId })
    });
    const result = await readJson(response);
    updateSession(result);
    return result;
  }, [getAccessToken, updateSession]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${apiRoot}/api/auth/logout`, {
        method: 'POST',
        headers: {
          ...clientPlatformHeaders,
          'X-Refresh-Token': getRefreshToken()
        }
      });
    } finally {
      updateToken('');
      setRefreshToken('');
    }
  }, [updateToken]);

  const claims = useMemo(() => accessToken ? parseToken(accessToken) : {}, [accessToken]);
  const email = claims.email || claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || '';
  const roleClaim = claims.role || claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '';
  const roleKey = (Array.isArray(roleClaim) ? roleClaim : roleClaim ? [roleClaim] : []).join('\u0000');
  const roles = useMemo(
    () => roleKey ? roleKey.split('\u0000') : [],
    [roleKey]
  );
  const role = roles[0] || '';
  const activeCompanyId = claims.CompanyId || claims.companyId || '';

  return (
    <AuthContext.Provider value={{
      isAuthenticated: Boolean(accessToken),
      isLoading,
      user: { email },
      role,
      roles,
      activeCompanyId,
      getAccessToken,
      sendLoginCode,
      verifyLoginCode,
      googleLogin,
      selectCompany,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider.');
  }
  return context;
}
